import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { InfoBox } from "@/components/InfoBox";
import { Typography } from "@/components/Typography";
import type { InterludeTypes } from "@/interlude";

/** Only what the card renders — the to-sign response carries more. */
interface ToSignContract {
  type: InterludeTypes["ContractType"];
  product: InterludeTypes["ContractProduct"];
  versionKey: string;
  pdfUrl: string | null;
}

const styles = {
  card: {
    flex: "1 1 480px",
    minWidth: 0,
    p: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  pdfFrame: {
    width: "100%",
    height: "560px",
    border: "1px solid",
    borderColor: "divider",
    borderRadius: "4px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

interface ContractCardProps {
  contract: ToSignContract;
  isChecked: boolean;
  /** Called with this card's versionKey; the parent owns the checked map. */
  onToggle: (versionKey: string, checked: boolean) => void;
}

/**
 * One contract to read and agree to.
 *
 * Its own component so the checkbox handler can go through `useHandler`:
 * versionKey comes from the parent's `.map()`, so there is no way to build a
 * per-card handler in the parent's scope.
 */
const ContractCard: React.FC<ContractCardProps> = function ContractCard({
  contract,
  isChecked,
  onToggle,
}) {
  const { t } = useTranslation();

  const contractTypeLabel = useMemo(
    () => ({
      msa: t("contract.type.msa", "Master Service Agreement"),
      addendum: t("contract.type.addendum", "Product Addendum"),
    }),
    [t],
  );

  const handleToggle = useHandler((e: React.ChangeEvent<HTMLInputElement>) => {
    onToggle(contract.versionKey, e.target.checked);
  });

  return (
    <Paper sx={styles.card}>
      <Box sx={styles.header}>
        <Typography variant="h3">{contractTypeLabel[contract.type]}</Typography>
        {contract.type === "addendum" && (
          <Chip label={contract.product.toUpperCase()} size="small" />
        )}
      </Box>
      <Typography variant="note">
        {t("contractSigning.version", "Version: {{versionKey}}", {
          versionKey: contract.versionKey,
        })}
      </Typography>
      {contract.pdfUrl ? (
        <Box
          component="iframe"
          src={contract.pdfUrl}
          // Describes the frame's purpose. The version key alone reads as a
          // meaningless digit string to a screen reader.
          title={contractTypeLabel[contract.type]}
          sx={styles.pdfFrame}
        />
      ) : (
        <InfoBox
          variant="warning"
          description={t(
            "contractSigning.pdfUnavailable",
            "The contract file is unavailable.",
          )}
        />
      )}
      <FormControlLabel
        control={
          <Checkbox
            checked={isChecked}
            // No file, no consent: "I have read and agree" cannot be true of a
            // document that was never displayed, and the agreement record is
            // permanent. The server refuses these too — this only keeps the UI
            // from inviting it.
            disabled={!contract.pdfUrl}
            onChange={handleToggle}
          />
        }
        label={t(
          "contractSigning.agreeCheckbox",
          "I have read and agree to this contract.",
        )}
      />
    </Paper>
  );
};

export { ContractCard };
export type { ToSignContract };
